//C:\VUE3\platform\src\common\axiosModule.js
// axiosModule.js
import axios from 'axios';
import { useMainStore } from '@/store/mainStore';
import userUtils from '@/common/userUtils';
import BASE_CONST from '@/common/variableConst';
import { logError, logWarn, logInfo, logDebug } from '@/utils/logHelper';


// ========================
// Utils
// ========================
function getSafeErrorMessage(error) {
  const isDev = process.env.NODE_ENV === 'development';
  const resp = error?.response;
  const data = resp?.data;

  const serverMessage =
    data?.message ||
    error?.errorResponse?.message ||
    '서버 오류가 발생했습니다.';

  // 개발: 그대로 노출
  if (isDev) return serverMessage;

  // 운영: 5xx만 숨김, 4xx는 그대로 노출
  const httpStatus = resp?.status ?? data?.statusCode ?? 0;
  if (httpStatus >= 500) return '서버 오류가 발생했습니다.';

  return serverMessage;
}

function buildApiError(error) {
  const resp = error?.response;
  const data = resp?.data;

  const httpStatus = resp?.status ?? data?.statusCode ?? 0;
  const code = data?.errorCode ?? 'UNKNOWN';

  // ✅ 여기서 운영에서도 4xx는 서버메시지 유지됨
  const message = getSafeErrorMessage(error);

  const details = data?.details ?? null;

  const err = new Error(message);
  err.httpStatus = httpStatus;
  err.code = code;
  err.details = details;

  // ✅ 원본 서버 메시지/데이터도 같이 보존(디버그/분기용)
  err.serverMessage = data?.message || null;
  err.serverData = data || null;

  err.errorResponse = {
    errorCode: code,
    message, // UI에 뿌릴 메시지(운영 4xx는 서버메시지)
    statusCode: httpStatus,
    details,
  };

  err.isDuplicateActiveEmail =
    httpStatus === 409 || code === 'DUPLICATE_ACTIVE_EMAIL';

  return err;
}

// ========================
// Token Refresh (de-dupe)
// ========================
let refreshingPromise = null;
async function tryRefreshToken() {
  if (refreshingPromise) return refreshingPromise;

  refreshingPromise = (async () => {
    try {
      const refreshToken = userUtils.getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token available');

      // 실제 Refresh API 호출 (서버 계약에 맞춰 body 형식 조정)
      const { data } = await axios.post(
        `${BASE_CONST.BASE_API_URL}/api/public/security/refresh`,
        refreshToken, // 혹은 { refreshToken }
        { withCredentials: true }
      );

      if (
        !data?.success ||
        !data?.content?.accessToken ||
        !data?.content?.refreshToken
      ) {
        throw new Error('Invalid refresh response');
      }

      const { accessToken, refreshToken: newRefresh } = data.content;
      userUtils.setAccessToken(accessToken);
      userUtils.setRefreshToken(newRefresh);

      logInfo('[AxiosModule] 토큰이 재발급되었습니다.');
      return true;
    } catch (error) {
      logError('[AxiosModule] 토큰 재발급 실패:', error);
      return false;
    } finally {
      refreshingPromise = null;
    }
  })();

  return refreshingPromise;
}

// ========================
// Axios Instance
// ========================
const instance = axios.create({
  baseURL: BASE_CONST.BASE_API_URL,
  withCredentials: true,
  timeout: 2 * 60 * 1000,
  responseType: 'json',
  responseEncoding: 'utf8',
});

// ========================
// Loading (Pinia 호환)
// ========================
let pending = 0;
function incLoading() {
  const s = useMainStore();
  if (typeof s.START === 'function') {
    s.START();
  } else if (typeof s.SET_LOADING === 'function') {
    pending++;
    s.SET_LOADING(pending > 0);
  }
}
function decLoading() {
  const s = useMainStore();
  if (typeof s.END === 'function') {
    s.END();
  } else if (typeof s.SET_LOADING === 'function') {
    pending = Math.max(0, pending - 1);
    s.SET_LOADING(pending > 0);
  }
}

// ========================
// Interceptors
// ========================

// (A) Request
instance.interceptors.request.use(
  (config) => {
    incLoading();
    config.headers['X-ACCESS-TOKEN'] = userUtils.getAccessToken();
      if (config.url?.includes('/api/public/security/refresh')) {
    config.headers['X-REFRESH-TOKEN'] = userUtils.getRefreshToken();
  }
    logDebug('[AxiosModule] 요청 설정 완료:', {
      url: config.url,
      method: config.method,
      headers: { ...config.headers, 'X-ACCESS-TOKEN': '***', 'X-REFRESH-TOKEN': '***' },
    });
    return config;
  },
  (error) => {
    decLoading();
    logError('[AxiosModule] 요청 설정 중 오류:', error);
    return Promise.reject(buildApiError(error));
  }
);

// (B) Response
instance.interceptors.response.use(
  // [B-1] 2xx 응답
  async (response) => {
    decLoading();

    // 파일 다운로드 등
    if (response.config.responseType === 'blob') {
      return response;
    }

    const data = response.data;

    // (1) ApiResponse 규격이 아닌 일반 응답: 그대로 전달
    if (!data || typeof data.success === 'undefined') {
      logWarn(
        '[AxiosModule] ApiResponse 규격 아님 → HTTP 성공으로 간주하여 raw data 반환'
      );
      return data;
    }

    // (2) success=true → content 반환
    if (data.success) {
      logDebug('[AxiosModule] 응답 - SUCCESS:', data);
      return data.content;
    }

    // (3) success=false → 바디형식 401(E401/UNAUTHORIZED) 대응
    const code = data?.errorCode;
    const statusCode = data?.statusCode ?? 400;

    const isUnauthorized =
      statusCode === 401 || code === 'E401' || code === 'UNAUTHORIZED';

    if (isUnauthorized) {
      const cfg = response.config;
      if (cfg._retry) {
        logWarn('[AxiosModule] 이미 재시도 중이었으나 다시 401(E401) 발생');
        return Promise.reject(buildApiError({ response }));
      }
      cfg._retry = true;

      const ok = await tryRefreshToken();
      if (ok) {
        logInfo('[AxiosModule] 토큰 재발급 성공 → 원 요청 재시도');
        cfg.headers['X-ACCESS-TOKEN'] = userUtils.getAccessToken();
        cfg.headers['X-REFRESH-TOKEN'] = userUtils.getRefreshToken();
        return instance(cfg);
      }
      logError('[AxiosModule] 토큰 재발급 실패 → 에러 반환');
      return Promise.reject(buildApiError({ response }));
    }

    // (4) 일반 비즈니스 에러
    // return Promise.reject(buildApiError({ response }));

        // ✅ (4) 일반 비즈니스 실패 → reject 대신 응답 그대로 반환
    // HTTP 200이지만 success: false인 경우 (정상적인 비즈니스 검증 실패)
    logDebug('[AxiosModule] 응답 - BUSINESS_FAIL:', data);
    return data;  // ← { success: false, message: "...", content: null }

  },

  // [B-2] 2xx 외(4xx/5xx)
  async (error) => {
    decLoading();

    const originalRequest = error.config;
    const statusCode = error.response?.status;
    const responseData = error.response?.data;

    // (1) HTTP 401 → 재발급 시도
    if (statusCode === 401) {
      logWarn('[AxiosModule] HTTP 401 인증 오류 발생');

      if (originalRequest?.url?.includes('/api/public/security/refresh')) {
        logError('[AxiosModule] /refresh 호출이 401 → 즉시 종료');
        return Promise.reject(buildApiError(error));
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        const success = await tryRefreshToken();
        if (success) {
          logInfo('[AxiosModule] 토큰 재발급 성공 → 원 요청 재시도');
          originalRequest.headers['X-ACCESS-TOKEN'] = userUtils.getAccessToken();
          originalRequest.headers['X-REFRESH-TOKEN'] = userUtils.getRefreshToken();
          return instance(originalRequest);
        } else {
          logError('[AxiosModule] 토큰 재발급 실패 → 에러 반환');
          return Promise.reject(buildApiError(error));
        }
      } else {
        logError('[AxiosModule] 재시도 후에도 401 발생 → 에러 반환');
        return Promise.reject(buildApiError(error));
      }
    }

    // (2) 서버가 ApiResponse { success:false } 구조로 내려줬을 때
    if (responseData && responseData.success === false) {
      return Promise.reject(buildApiError(error));
    }

    // (3) 그 외 일반 에러
    return Promise.reject(buildApiError(error));
  }
);

// ========================
// HTTP helpers
// ========================
function get(url, params) {
  return instance.get(url, { params });
}
function post(url, data) {
  return instance.post(url, data);
}
function postMultipart(url, formData) {
  return instance.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}
function postBlob(url, data) {
  return instance.post(url, data, { responseType: 'blob' });
}
function put(url, data) {
  return instance.put(url, data);
}
function patch(url, data) {
  return instance.patch(url, data);
}
function patchMultipart(url, formData) {
  return instance.patch(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}
function deleteMethod(url, params) {
  return instance.delete(url, { params });
}

export default {
  get,
  post,
  postMultipart,
  postBlob,
  put,
  patch,
  patchMultipart,
  delete: deleteMethod,
  instance, // 필요 시 직접 접근
};
